import { useState, useEffect } from 'react';

export default function Preloader({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState(0); // 0: loading, 1: revealing, 2: done

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(timer);
          setTimeout(() => setPhase(1), 300);
          setTimeout(() => {
            setPhase(2);
            onComplete?.();
          }, 1200);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [onComplete]);

  if (phase === 2) return null;

  return (
    <div
      className={`fixed inset-0 z-[10000] flex items-center justify-center bg-[#050505] transition-all duration-700 ${
        phase === 1 ? 'opacity-0 scale-110' : 'opacity-100 scale-100'
      }`}
    >
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Radial glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#ff5a1f]/10 rounded-full blur-[100px] animate-pulse" />
        
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(#ff5a1f 1px, transparent 1px), linear-gradient(90deg, #ff5a1f 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }} />
        </div>

        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#ff5a1f] rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 text-center">
        {/* Animated Logo */}
        <div className="mb-12 relative">
          <div className="relative inline-block">
            {/* Glowing ring */}
            <div className="absolute -inset-8 rounded-full border border-[#ff5a1f]/30 animate-spin-slow" />
            <div className="absolute -inset-12 rounded-full border border-[#ff5a1f]/20 animate-spin-reverse" />
            <div className="absolute -inset-16 rounded-full border border-[#ff5a1f]/10 animate-spin-slow" />
            
            {/* Logo icon */}
            <div className="w-24 h-24 relative">
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {/* Animated W shape */}
                <path
                  d="M10 30 L25 70 L40 40 L55 70 L70 30"
                  fill="none"
                  stroke="#ff5a1f"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-draw"
                  style={{
                    strokeDasharray: 200,
                    strokeDashoffset: 200 - (progress * 2),
                  }}
                />
                {/* Bang dot */}
                <circle
                  cx="85"
                  cy="30"
                  r="6"
                  fill="#ff5a1f"
                  className="animate-pulse"
                  style={{ opacity: progress > 80 ? 1 : 0 }}
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Brand text with reveal animation */}
        <div className="overflow-hidden mb-8">
          <h1 
            className="font-syne text-5xl md:text-7xl font-bold text-white tracking-tight"
            style={{
              transform: `translateY(${100 - progress}%)`,
              transition: 'transform 0.5s ease-out',
            }}
          >
            <span className="text-[#ff5a1f]">Wiz</span>bang
          </h1>
        </div>

        {/* Tagline */}
        <div className="overflow-hidden mb-12">
          <p 
            className="font-mono text-sm text-[#888] tracking-widest uppercase"
            style={{
              opacity: progress > 50 ? 1 : 0,
              transform: `translateY(${progress > 50 ? 0 : 20}px)`,
              transition: 'all 0.5s ease-out',
            }}
          >
            Building Revenue Systems
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-64 mx-auto">
          <div className="relative h-[2px] bg-[#1F1F1F] overflow-hidden rounded-full">
            <div 
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#ff5a1f] to-[#ff8a5f] rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
            {/* Shimmer effect */}
            <div 
              className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"
              style={{ transform: 'translateX(-100%)' }}
            />
          </div>
          <p className="mt-4 font-mono text-xs text-[#555]">
            {Math.round(Math.min(progress, 100))}%
          </p>
        </div>

        {/* Loading text */}
        <div className="mt-8 flex items-center justify-center gap-2">
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-[#ff5a1f] rounded-full animate-bounce"
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
          <span className="text-[#555] text-sm ml-2">
            {progress < 30 ? 'Initializing' : progress < 60 ? 'Loading assets' : progress < 90 ? 'Almost ready' : 'Launching'}
          </span>
        </div>
      </div>
    </div>
  );
}
